import React, { useEffect, useRef, useState } from "react";
import "./CompanyProfile.css";
import StatsSection from "./StatsSection";

/* ── Scroll-reveal hook ──────────────────────────────────── */
function useScrollReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, revealed];
}

// Helper function to add trademark symbol to Arklon
const addTrademarkToArklon = (text) => {
  // Replace Arklon with Arklon® (case sensitive)
  return text.replace(/Arklon/g, 'Arklon®');
};

/* ── Static data: exclusively lap spool focused ──────────── */
const ARKLON_SPECS = [
  "High Load-Bearing for Spools",
  "Extreme Wear Resistance",
  "Corrosion Proof Lap Spools",
  "Ultra-Low Friction Finish",
  "Zero Maintenance Operation",
  "Extended Spool Lifecycle",
];

const ABOUT_FEATURES = [
  {
    id: "01",
    title: "LapSpool Manufacturing Facility",
    desc: "Dedicated in-house tool room & heavy machining center for lap spools.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 9h6M9 12h6M9 15h4" />
      </svg>
    ),
  },
  {
    id: "02",
    title: "Quality Certified Spools",
    desc: "Strictly GSFC-tested raw materials for premium lap spools.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="8" r="6" />
        <polyline points="8.5 14 7 23 12 20 17 23 15.5 14" />
      </svg>
    ),
  },
  {
    id: "03",
    title: "LapSpool Engineering Experts",
    desc: "29+ years of cumulative technical expertise in lap spool design.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: "04",
    title: "Global Spool Supply",
    desc: "Exceeding lap spool demands across worldwide textile markets.",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
        <line x1="2" y1="12" x2="22" y2="12" />
      </svg>
    ),
  },
];

/* ── Component ───────────────────────────────────────────── */
export default function CompanyProfile() {
  const [arklonImgRef, arklonImgRevealed] = useScrollReveal(0.12);
  const [arklonTextRef, arklonTextRevealed] = useScrollReveal(0.08);
  const [aboutImgRef, aboutImgRevealed] = useScrollReveal(0.12);
  const [aboutTextRef, aboutTextRevealed] = useScrollReveal(0.08);

  return (
    <div className="emk-cp-wrapper">
      <StatsSection />

      {/* ════════════════════════════════
          ARKLON — lap spool material spotlight
      ════════════════════════════════ */}
      <section className="emk-cp-section emk-cp-section--arklon">
        <div className="emk-cp-two-col">
          {/* Text side */}
          <div
            ref={arklonTextRef}
            className={`emk-cp-text-side${arklonTextRevealed ? " emk-cp-text-side--in" : ""}`}
          >
            <span className="emk-cp-eyebrow">Arklon® Material Innovation</span>

            <h2 className="emk-cp-section-heading">
              Arklon® —
              <span className="emk-cp-section-heading-soft">
                <br />
                The Future of
                <br />
                Lap Spools.
              </span>
            </h2>

            <p className="emk-cp-para-lead">
              <strong className="emk-brand-highlight">Arklon®</strong> — an
              evolutionary engineering polyamide engineered specifically for
              premium lap spools, replacing outdated Hylam
              materials.
            </p>
            <p className="emk-cp-para-body">
              Designed explicitly for rigorous textile workloads,{" "}
              <strong className="emk-brand-highlight">Arklon®</strong>{" "}
              drastically enhances spool processing efficiencies
              while ensuring absolute operational scaling for lap winding
              applications.
            </p>

            <ul className="emk-cp-spec-list">
              {ARKLON_SPECS.map((label, i) => (
                <li
                  key={i}
                  className="emk-cp-spec-list__item"
                  style={{ "--emk-cp-stagger": `${i * 0.07}s` }}
                >
                  <span className="emk-cp-spec-list__dot" aria-hidden="true" />
                  {label.includes("Spool") ? (
                    <>
                      <span className="emk-brand-highlight">Arklon®</span>{" "}
                      {label}
                    </>
                  ) : (
                    label
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Image side — Arklon lap spool imagery */}
          <div
            ref={arklonImgRef}
            className={`emk-cp-img-side${arklonImgRevealed ? " emk-cp-img-side--in" : ""}`}
          >
            <div className="emk-cp-arklon-frame">
              {/* Block-wipe reveal shroud */}
              <div className="emk-cp-wipe-shroud" aria-hidden="true" />

              <img
                src="/assets/lh15.png" // Replace with actual Arklon lap spool image
                alt="Arklon® Lap Spool Component"
                className="emk-cp-arklon-img"
              />

              {/* Decorative corner brackets */}
              <span
                className="emk-cp-bracket emk-cp-bracket--tl"
                aria-hidden="true"
              />
              <span
                className="emk-cp-bracket emk-cp-bracket--tr"
                aria-hidden="true"
              />
              <span
                className="emk-cp-bracket emk-cp-bracket--bl"
                aria-hidden="true"
              />
              <span
                className="emk-cp-bracket emk-cp-bracket--br"
                aria-hidden="true"
              />

              {/* Floating material chip — Arklon highlighted */}
              <div className="emk-cp-material-chip">
                <span
                  className="emk-cp-material-chip__pulse"
                  aria-hidden="true"
                />
                <span className="emk-brand-highlight">Arklon®</span> Lap Spools
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="emk-cp-rule" />

      {/* ════════════════════════════════
          ABOUT — Arklon lap spool company profile
      ════════════════════════════════ */}
      <section id="about" className="emk-cp-section emk-cp-section--about">
        <div className="emk-cp-two-col emk-cp-two-col--flipped">
          {/* Image side — lap spool manufacturing facility */}
          <div
            ref={aboutImgRef}
            className={`emk-cp-img-side${aboutImgRevealed ? " emk-cp-img-side--in" : ""}`}
          >
            <div className="emk-cp-about-photo-wrap">
              {/* Block-wipe reveal shroud */}
              <div className="emk-cp-wipe-shroud" aria-hidden="true" />

              <img
                src="/assets/comber.png" // Replace with actual Arklon facility/spool image
                alt="Arklon® Lap Spool Manufacturing Facility"
                className="emk-cp-about-photo"
              />

              {/* Glass badge overlay — Arklon focused */}
              <div className="emk-cp-glass-badge">
                <span className="emk-cp-glass-badge__number">
                  29<sup>+</sup>
                </span>
                <span className="emk-cp-glass-badge__caption">
                  Years of Arklon® Lap Spool Excellence
                </span>
              </div>
            </div>
          </div>

          {/* Text side */}
          <div
            ref={aboutTextRef}
            className={`emk-cp-text-side${aboutTextRevealed ? " emk-cp-text-side--in" : ""}`}
          >
            <span className="emk-cp-eyebrow">Arklon® — Company Profile</span>

            <h2 className="emk-cp-section-heading">
              One and only Arklon® Lap Spools
              <span className="emk-cp-section-heading-soft">
                <br />
                Manufacturer in India.
              </span>
            </h2>

            <p className="emk-cp-para-lead">
              Headquartered in Palakkad, Kerala,{" "}
              <strong className="emk-brand-highlight">Emmark Industries</strong>{" "}
              (Est. 1997) builds high-precision lap spools for tier-1 textile manufacturers.
            </p>

            {/* Feature cards grid — all lap spool focused */}
            <div className="emk-cp-feat-grid">
              {ABOUT_FEATURES.map((feat, i) => (
                <div
                  key={feat.id}
                  className="emk-cp-feat-card"
                  style={{ "--emk-cp-stagger": `${i * 0.08}s` }}
                >
                  <div className="emk-cp-feat-card__top">
                    <span className="emk-cp-feat-card__index">{feat.id}</span>
                    <span className="emk-cp-feat-card__icon">{feat.icon}</span>
                  </div>
                  <h4 className="emk-cp-feat-card__title">
                    <span className="emk-brand-highlight">Arklon®</span>{" "}
                    {feat.title}
                  </h4>
                  <p className="emk-cp-feat-card__desc">{feat.desc}</p>
                </div>
              ))}
            </div>

            <a href="/about" className="emk-cp-underline-cta">
              <span>Discover Arklon® Lap Spool Excellence</span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <hr className="emk-cp-rule" />
    </div>
  );
}